import {
  toWorkspaceAgentBoardTreeBrowseLevel,
  WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS,
  type WorkspaceAgentBoardTreeLevel,
  type WorkspaceAgentBoardTreeValue,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/flattenWorkspaceAgentBoardTree';
import { type WorkspaceAgentRootBoardsByLibraryId } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/groupWorkspaceAgentRootBoardsByLibrary';
import { type WorkspaceAgentBoardMentionSource } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentBoardMentionItem';
import {
  type WorkspaceAgentBoardMentionItem,
  type WorkspaceAgentLibraryMentionSource,
  type WorkspaceAgentMentionItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { buildTreeFromAncestors, type TreePathEntry } from '~/utils/tree/buildTreeFromAncestors';

/** Nothing is fetched under a query, so the roots a library falls back to hold no children. */
const NO_FETCHED_CHILDREN: Record<string, WorkspaceAgentBoardMentionSource[]> = {};

export type GroupWorkspaceAgentBoardSearchMatchesParams = {
  /** Present when named libraries exist so no-library hits sit under Global, first in that group. */
  generalLibrary: WorkspaceAgentLibraryMentionSource | undefined;
  generalRootBoards: readonly WorkspaceAgentBoardMentionSource[];
  libraries: WorkspaceAgentLibraryMentionSource[];
  /** The search rows; anything that is not a board is ignored. */
  matches: WorkspaceAgentMentionItem[];
  query: string;
  rootBoardsByLibraryId: WorkspaceAgentRootBoardsByLibraryId;
};

/** The hit and the ancestors it hangs under, as the nodes the tree is built from. */
const toWorkspaceAgentBoardMatchPath = (
  match: WorkspaceAgentBoardMentionItem,
): TreePathEntry<WorkspaceAgentBoardTreeValue>[] => {
  const ancestors: TreePathEntry<WorkspaceAgentBoardTreeValue>[] = match.ancestors.map((ancestor) => ({
    id: ancestor.id,
    value: { board: ancestor, kind: 'board' },
  }));
  const hit: TreePathEntry<WorkspaceAgentBoardTreeValue> = {
    id: match.boardId,
    value: {
      board: {
        coverImage: match.coverImageUrl ? { url: match.coverImageUrl } : undefined,
        id: match.boardId,
        // Carried through so a board found by search draws the same row as the same board browsed.
        library: match.library,
        title: match.title,
      },
      kind: 'board',
    },
  };

  return [...ancestors, hit];
};

/** A board arrives first as an ancestor (title only) and later as a match of its own. */
const mergeWorkspaceAgentBoardTreeValues = (
  existing: WorkspaceAgentBoardTreeValue,
  incoming: WorkspaceAgentBoardTreeValue,
): WorkspaceAgentBoardTreeValue =>
  existing.kind === 'board' && incoming.kind === 'board'
    ? { board: { ...existing.board, ...incoming.board }, kind: 'board' }
    : incoming;

const buildWorkspaceAgentBoardMatchTree = (matches: WorkspaceAgentBoardMentionItem[]) =>
  buildTreeFromAncestors(matches, {
    mergeValues: mergeWorkspaceAgentBoardTreeValues,
    toPath: toWorkspaceAgentBoardMatchPath,
  });

/**
 * A library matched by name has no board hit, so it opens onto its roots.
 */
const toLibrarySearchChildren = (
  libraryMatches: WorkspaceAgentBoardMentionItem[] | undefined,
  fallbackBoards: readonly WorkspaceAgentBoardMentionSource[],
) =>
  libraryMatches
    ? buildWorkspaceAgentBoardMatchTree(libraryMatches)
    : toWorkspaceAgentBoardTreeBrowseLevel(fallbackBoards, NO_FETCHED_CHILDREN, false);

/** Global first when it matched, then named libraries in the order they were discovered. */
const toOrderedLibrarySearchSources = (
  matchedLibraries: Map<string, WorkspaceAgentLibraryMentionSource>,
  globalLibrary: WorkspaceAgentLibraryMentionSource | undefined,
): WorkspaceAgentLibraryMentionSource[] => {
  if (!globalLibrary) {
    return [...matchedLibraries.values()];
  }

  const namedLibraries = [...matchedLibraries.values()].filter((library) => library.id !== globalLibrary.id);

  if (!matchedLibraries.has(globalLibrary.id)) {
    return namedLibraries;
  }

  return [globalLibrary, ...namedLibraries];
};

/**
 * The board matches as a tree: every match keeps its place under its ancestors and its library — a
 * match only means something next to where it lives.
 *
 * A library matched by its own name has no matching board to show, so it opens onto its roots
 * instead — a heading that cannot be opened is a dead row. One with nothing under it at all is
 * dropped rather than offered as an empty match.
 */
export const groupWorkspaceAgentBoardSearchMatches = ({
  generalLibrary,
  generalRootBoards,
  libraries,
  matches,
  query,
  rootBoardsByLibraryId,
}: GroupWorkspaceAgentBoardSearchMatchesParams): WorkspaceAgentBoardTreeLevel => {
  const generalMatches: WorkspaceAgentBoardMentionItem[] = [];
  const matchesByLibraryId = new Map<string, WorkspaceAgentBoardMentionItem[]>();
  const matchedLibraries = new Map<string, WorkspaceAgentLibraryMentionSource>();
  const normalizedQuery = query.trim().toLowerCase();
  const globalLibrary = libraries.length > 0 ? generalLibrary : undefined;

  // A library is a match in its own right, or because something under it matched.
  if (normalizedQuery !== '') {
    if (globalLibrary?.title.toLowerCase().includes(normalizedQuery)) {
      matchedLibraries.set(globalLibrary.id, globalLibrary);
    }

    libraries.forEach((library) => {
      if (library.title.toLowerCase().includes(normalizedQuery)) {
        matchedLibraries.set(library.id, library);
      }
    });
  }

  matches.forEach((item) => {
    if (item.kind !== 'board') {
      return;
    }

    const { library } = item;

    if (!library) {
      generalMatches.push(item);

      return;
    }

    matchedLibraries.set(library.id, library);

    const libraryMatches = matchesByLibraryId.get(library.id);

    if (libraryMatches) {
      libraryMatches.push(item);
    } else {
      matchesByLibraryId.set(library.id, [item]);
    }
  });

  if (globalLibrary && generalMatches.length > 0) {
    matchedLibraries.set(globalLibrary.id, globalLibrary);
    matchesByLibraryId.set(globalLibrary.id, generalMatches);
  }

  const roots: WorkspaceAgentBoardTreeLevel = globalLibrary
    ? new Map()
    : buildWorkspaceAgentBoardMatchTree(generalMatches);

  const orderedLibraries = toOrderedLibrarySearchSources(matchedLibraries, globalLibrary);

  for (const library of orderedLibraries) {
    const fallbackBoards =
      globalLibrary && library.id === globalLibrary.id
        ? generalRootBoards
        : (rootBoardsByLibraryId[library.id] ?? WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS);
    const children = toLibrarySearchChildren(matchesByLibraryId.get(library.id), fallbackBoards);

    if (children.size === 0) {
      continue;
    }

    roots.set(library.id, {
      children,
      id: library.id,
      isExpandedByDefault: true,
      isMatch: false,
      value: { kind: 'library', library },
    });
  }

  return roots;
};
